import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function TournamentAdmin() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState(null);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL;

  const checkHealth = async () => {
    setHealthLoading(true);
    setHealthError(null);
    
    try {
      const token = localStorage.getItem('arena_token');
      
      if (!token) {
        setHealthError('No auth token found. Please log in.');
        setHealthLoading(false);
        return;
      }

      const response = await fetch(`${API}/api/admin/tournaments/health`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setHealthError('Unauthorized. Please log in again.');
        } else if (response.status === 403) {
          setHealthError('Forbidden. Admin access required.');
        } else {
          const data = await response.json().catch(() => ({ detail: 'Unknown error' }));
          setHealthError(`Error ${response.status}: ${data.detail || 'Failed to fetch health'}`);
        }
        setHealthLoading(false);
        return;
      }

      const data = await response.json();
      setHealth(data);
      setHealthError(null);
    } catch (err) {
      console.error('Health check error:', err);
      setHealthError(`Network error: ${err.message}`);
    } finally {
      setHealthLoading(false);
    }
  };

  const triggerTournaments = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('arena_token');
      if (!token) {
        setError('Not logged in. Please refresh the page and log in again.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API}/api/admin/tournaments/advance-now`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to advance tournaments');
      }

      setResult(data);
      // Refresh health after triggering
      setTimeout(() => checkHealth(), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load health status on mount
  useEffect(() => {
    // Small delay to ensure component is fully mounted
    const timer = setTimeout(() => {
      checkHealth().catch(err => {
        console.error('Initial health check failed:', err);
        setHealthError(`Failed to load: ${err.message}`);
      });
    }, 100);
    
    // Refresh health every 30 seconds
    const interval = setInterval(() => {
      checkHealth().catch(err => {
        console.error('Periodic health check failed:', err);
      });
    }, 30000);
    
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">
          🏆 Tournament Admin
        </h1>

        {/* Health Status */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">System Status</h2>
            <button
              onClick={checkHealth}
              disabled={healthLoading}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              {healthLoading ? '⏳ Checking...' : '🔄 Refresh'}
            </button>
          </div>

          {healthError ? (
            <div className="text-red-600 text-sm bg-red-50 p-4 rounded border border-red-200">
              <strong>Health Check Error:</strong> {healthError}
            </div>
          ) : healthLoading ? (
            <div className="text-gray-500 text-sm">Loading health status...</div>
          ) : health ? (
            <div className="space-y-3">
              {/* Overall Status */}
              <div className={`p-3 rounded-lg ${
                health.diagnosis?.severity === 'healthy' 
                  ? 'bg-green-50 border border-green-200'
                  : health.diagnosis?.severity === 'critical'
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className="font-semibold text-sm">
                  {health.diagnosis?.severity === 'healthy' ? '✅ Healthy' : '⚠️ Issues Detected'}
                </div>
              </div>

              {/* Worker Status */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-600 font-medium">Background Worker</div>
                  <div className={`font-semibold ${
                    health.worker?.status === 'running' ? 'text-green-600' :
                    health.worker?.status === 'stalled' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {health.worker?.status === 'running' ? '✅ Running' :
                     health.worker?.status === 'stalled' ? '⚠️ Stalled' :
                     '❌ Not Running'}
                  </div>
                  {health.worker?.last_heartbeat && (
                    <div className="text-xs text-gray-500 mt-1">
                      Last seen: {Math.round(health.worker.seconds_since_heartbeat)}s ago
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-gray-600 font-medium">Auto-Advancement</div>
                  <div className={`font-semibold ${
                    health.advancement?.status === 'active' ? 'text-green-600' :
                    health.advancement?.status === 'stalled' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {health.advancement?.status === 'active' ? '✅ Active' :
                     health.advancement?.status === 'stalled' ? '⚠️ Stalled' :
                     '❌ Never Run'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Total runs: {health.advancement?.total_runs || 0}
                  </div>
                </div>
              </div>

              {/* Issues */}
              {health.diagnosis?.issues?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <div className="font-semibold text-sm text-red-800 mb-2">Issues:</div>
                  <ul className="text-xs text-red-700 space-y-1 list-disc list-inside">
                    {health.diagnosis.issues.map((issue, i) => (
                      <li key={i}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recent Logs */}
              {health.recent_logs && health.recent_logs.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 mt-3">
                  <div className="font-semibold text-sm text-gray-800 mb-2">Recent Activity (last 20 logs):</div>
                  <div className="text-xs text-gray-700 space-y-1 max-h-48 overflow-y-auto font-mono">
                    {health.recent_logs.slice().reverse().map((log, i) => (
                      <div key={i} className="border-b border-gray-200 pb-1">
                        <span className="text-gray-500">{new Date(log.time).toLocaleTimeString()}</span>
                        {' - '}
                        <span>{log.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-gray-500 text-sm">No health data available. Click refresh.</div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h2 className="text-2xl font-semibold mb-4">Emergency Tournament Control</h2>
          <p className="text-gray-600 mb-6">
            If tournaments are stuck or not advancing automatically, click this button to manually trigger tournament advancement.
          </p>

          <button
            onClick={triggerTournaments}
            disabled={loading}
            className={`w-full py-4 px-6 rounded-lg font-bold text-xl transition-all ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {loading ? '⏳ Advancing Tournaments...' : '🚀 START TOURNAMENTS NOW'}
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-6">
            <h3 className="font-bold mb-2">❌ Error</h3>
            <p>{error}</p>
          </div>
        )}

        {result && (
          <div className="bg-green-100 border border-green-400 text-green-800 px-6 py-4 rounded-lg">
            <h3 className="font-bold mb-4">✅ Success!</h3>
            <div className="space-y-2 text-sm">
              <p><strong>Message:</strong> {result.message}</p>
              <p><strong>Time:</strong> {new Date(result.timestamp).toLocaleString()}</p>
              <p><strong>Week:</strong> {result.week_id}</p>
              
              {result.tournaments && Object.keys(result.tournaments).length > 0 && (
                <div className="mt-4">
                  <p className="font-semibold mb-2">Tournament Status:</p>
                  <div className="bg-white rounded p-3 space-y-1">
                    {Object.entries(result.tournaments).map(([bracket, instances]) => (
                      <div key={bracket}>
                        <strong className="text-xs uppercase">{bracket}:</strong>
                        {instances.map((inst, i) => (
                          <span key={i} className="ml-2 text-xs">
                            Instance {inst.instance}: {inst.status} (Round {inst.current_round})
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-green-300">
                <p className="text-xs">
                  ✨ Refresh the tournaments page to see the changes!
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/tournaments')}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            ← Back to Tournaments
          </button>
        </div>
      </div>
    </div>
  );
}
